import {
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Tooltip
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import { makeStyles } from "@material-ui/core/styles";
import { InfoOutlined } from "@material-ui/icons";
import { CardSpacer } from "@saleor/components/CardSpacer";
import CardTitle from "@saleor/components/CardTitle";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import { Container } from "@saleor/components/Container";
import ControlledCheckbox from "@saleor/components/ControlledCheckbox";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import Grid from "@saleor/components/Grid";
import Hr from "@saleor/components/Hr";
import Metadata from "@saleor/components/Metadata/Metadata";
import { MetadataFormData } from "@saleor/components/Metadata/types";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import SeoForm from "@saleor/components/SeoForm";
import VisibilityCard from "@saleor/components/VisibilityCard";
import { ProductErrorFragment } from "@saleor/fragments/types/ProductErrorFragment";
import useDateLocalize from "@saleor/hooks/useDateLocalize";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { getChoices } from "@saleor/products/utils/data";
import { CoverageTypeEnum, PageTypeEnum } from "@saleor/types/globalTypes";
import createMultiAutocompleteSelectHandler from "@saleor/utils/handlers/multiAutocompleteSelectChangeHandler";
import { mapMetadataItemToInput } from "@saleor/utils/maps";
import useMetadataChangeTrigger from "@saleor/utils/metadata/useMetadataChangeTrigger";
import { RawDraftContentState } from "draft-js";
import React, { Fragment, useState } from "react";
import { useIntl } from "react-intl";

import { maybe } from "../../../misc";
import { ListActions, PageListProps } from "../../../types";
import { CollectionDetails_collection } from "../../types/CollectionDetails";
import CollectionDeepLink from "../CollectionDeepLink";
import CollectionDetails from "../CollectionDetails/CollectionDetails";
import CollectionHeader from "../CollectionHeader";
import { CollectionImage } from "../CollectionImage/CollectionImage";
import CollectionProducts from "../CollectionProducts/CollectionProducts";
import CollectionThumbnailImage from "../CollectionThumbnailImage";
const useStyles = makeStyles(
  {
    frmAction: {
      justifyContent: "center"
    },
    frmBtn: {
      "&.active": {
        "& span": {
          color: "#fff"
        },
        backgroundColor: "#285BAD"
      },
      "&:hover": {
        "& span": {
          color: "#fff"
        },
        backgroundColor: "#285BAD"
      },
      color: "#0030FF",
      padding: "6px 30px"
    },
    frmTxt: {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      fontSize: "18px",
      justifyContent: "center",
      minHeight: "100px"
    }
  },
  { name: "CategoryUpdatePage" }
);

export interface CollectionDetailsPageFormData extends MetadataFormData {
  backgroundImageAlt: string;
  description: RawDraftContentState;
  thumbnailLinkImage: {
    alt: string;
    url: string;
  };
  name: string;
  slug: string;
  color: string;
  publicationDate: string;
  seoDescription: string;
  seoTitle: string;
  iconImage: {
    alt: string;
    url: string;
  };
  isFromShopify?: boolean;
  isFeaturedOnSmFulfilledPage?: boolean;
  isFeatured: boolean;
  isPublished: boolean;
  featuredIn: PageTypeEnum[];
  isDirectPurchaseEnabled: boolean;
}

export interface CollectionDetailsPageProps extends PageListProps, ListActions {
  collection: CollectionDetails_collection | null;
  errors: ProductErrorFragment[];
  isFeatured: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onBack: () => void;
  onCollectionRemove: () => void;
  onThumbnailLinkImageUpload: (file: File) => void;
  onIconUpload: (file: File) => void;
  onImageDelete: () => void;
  onImageUpload: (file: File) => void;
  onProductUnassign: (id: string, event: React.MouseEvent<any>) => void;
  onSubmit: (data: CollectionDetailsPageFormData) => void;
  onGenerateDeeplink: (id: string) => void;
  onFileSizeError: () => void;
}

const CollectionDetailsPage: React.FC<CollectionDetailsPageProps> = ({
  collection,
  disabled,
  errors,
  isFeatured,
  saveButtonBarState,
  onBack,
  onCollectionRemove,
  onIconUpload,
  onImageDelete,
  onImageUpload,
  onThumbnailLinkImageUpload,
  onSubmit,
  onGenerateDeeplink,
  onFileSizeError,
  ...collectionProductsProps
}: CollectionDetailsPageProps) => {
  const classes = useStyles("");
  const intl = useIntl();
  const localizeDate = useDateLocalize();
  const {
    isMetadataModified,
    isPrivateMetadataModified,
    makeChangeHandler: makeMetadataChangeHandler
  } = useMetadataChangeTrigger();

  const handleSubmit = () => {
    dialogConfirm("Are you sure you want to save this collection?");
  };
  const [title, setTitle] = useState("");
  const [open, setOpen] = useState(false);
  const callback = (data, click) => {
    if (click) {
      const metadata = isMetadataModified ? data.metadata : undefined;
      const privateMetadata = isPrivateMetadataModified
        ? data.privateMetadata
        : undefined;

      onSubmit({
        ...data,
        isPublished: data.isPublished || !!data.publicationDate,
        metadata,
        privateMetadata
      });
    }
    setOpen(false);
  };
  const dialogConfirm = message => {
    setTitle(message);
    setOpen(true);
  };
  const hideMeta = true;
  const hideSEO = true;

  const featuredInChoices = [
    {
      label: "Food",
      value: PageTypeEnum.FOOD
    },
    {
      label: "Grocery",
      value: PageTypeEnum.GROCERY
    },
    {
      label: "ShopSM",
      value: PageTypeEnum.SM_FULFILLED
    },
    {
      label: "Shops",
      value: PageTypeEnum.SHOPS
    },
    {
      label: "Cinema",
      value: PageTypeEnum.CINEMA
    },
    {
      label: "Discounts",
      value: PageTypeEnum.DISCOUNTS
    }
  ];
  const featuredInProperCase = (featured: PageTypeEnum) => {
    switch (featured) {
      case PageTypeEnum.FOOD:
        return "Food";
      case PageTypeEnum.GROCERY:
        return "Grocery";
      case PageTypeEnum.SM_FULFILLED:
        return "ShopSM";
      case PageTypeEnum.SHOPS:
        return "Shops";
      case PageTypeEnum.CINEMA:
        return "Cinema";
      case PageTypeEnum.DISCOUNTS:
        return "Discounts";
      default:
        return " ";
    }
  };

  const selectedFeaturedIn = maybe(() =>
    collection.featuredIn.map(featuredIn => ({
      id: featuredIn,
      name: featuredInProperCase(featuredIn)
    }))
  );

  const [selectedFeatured, setSelectedFeatured] = useStateFromProps(
    getChoices(selectedFeaturedIn || [])
  );

  return (
    <Form
      initial={{
        backgroundImageAlt: maybe(() => collection?.backgroundImage?.alt, ""),
        color: maybe(() => collection?.color, ""),
        description: maybe(() => JSON.parse(collection?.descriptionJson || "")),
        featuredIn: collection?.featuredIn || [],
        iconImage: {
          alt: maybe(() => collection?.iconImage?.alt, ""),
          url: maybe(() => collection?.iconImage?.url, "")
        },
        isFeatured: maybe(() => collection?.isFeatured, false),
        isFeaturedOnSmFulfilledPage: collection?.isFeaturedOnSmFulfilledPage,
        isFromShopify: collection?.isFromShopify,
        isPublished: maybe(() => collection.isPublished, false),
        metadata: collection?.metadata?.map(mapMetadataItemToInput),
        name: maybe(() => collection?.name, ""),
        privateMetadata: collection?.privateMetadata?.map(
          mapMetadataItemToInput
        ),
        publicationDate: maybe(() => collection?.publicationDate, ""),
        seoDescription: maybe(() => collection?.seoDescription, ""),
        seoTitle: maybe(() => collection?.seoTitle, ""),
        slug: collection?.slug || "",
        thumbnailLinkImage: {
          alt: maybe(() => collection?.thumbnailLinkImage?.alt, ""),
          url: maybe(() => collection?.thumbnailLinkImage?.url, "")
        },
        isDirectPurchaseEnabled: maybe(
          () => collection?.isDirectPurchaseEnabled,
          false
        )
      }}
      onSubmit={handleSubmit}
      confirmLeave
    >
      {({ change, data, hasChanged, submit, toggleValue }) => {
        const changeMetadata = makeMetadataChangeHandler(change);
        if (!data.isPublished) {
          data.isFeatured = false;
        }

        const handleSelectFeatured = createMultiAutocompleteSelectHandler(
          toggleValue,
          setSelectedFeatured,
          selectedFeatured,
          featuredInChoices
        );

        const breadcrumbDirectory = [
          {
            name: "Collections",
            url: "/collections"
          },
          {
            name: maybe(() => collection?.name)
          }
        ];

        return (
          <Container>
            <PageBreadcrumbNavigation items={breadcrumbDirectory} />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                marginTop: "10px"
              }}
            >
              <PageBackNavigation onBack={onBack} />
              <PageHeader title={maybe(() => collection?.name)} />
            </div>
            <Grid>
              <div>
                <CollectionDetails
                  collection={collection}
                  data={data}
                  disabled={disabled}
                  errors={errors}
                  onChange={change}
                  featuredInDisplayValue={selectedFeatured}
                  featuredChoices={featuredInChoices}
                  setSelectedFeatured={setSelectedFeatured}
                  onFeaturedChange={handleSelectFeatured}
                />
                <CardSpacer />
                <CollectionDeepLink
                  disabled={collection?.isFromShopify}
                  linkId={collection && collection.id}
                  linkUrl={collection && collection.deeplinkUrl}
                  shortLinkUrl={collection && collection.shortDynamicLink}
                  onGenerateDeeplink={onGenerateDeeplink}
                />
                <CardSpacer />
                <CollectionImage
                  data={data}
                  image={maybe(() => collection.backgroundImage)}
                  onImageDelete={onImageDelete}
                  onImageUpload={onImageUpload}
                  onChange={change}
                  onFileSizeError={onFileSizeError}
                />
                {!hideMeta ? (
                  <Fragment>
                    <CardSpacer />
                    <Metadata data={data} onChange={changeMetadata} />
                  </Fragment>
                ) : (
                  ""
                )}
                <CardSpacer />
                <CollectionHeader
                  data={data}
                  onChange={change}
                  onIconUpload={file => onIconUpload(file)}
                />
                <CardSpacer />
                <CollectionProducts
                  disabled={disabled || collection?.isFromShopify}
                  collection={collection}
                  {...collectionProductsProps}
                />
                {!hideSEO ? (
                  <Fragment>
                    <CardSpacer />
                    <SeoForm
                      description={data.seoDescription}
                      disabled={disabled}
                      descriptionPlaceholder=""
                      helperText={intl.formatMessage({
                        defaultMessage:
                          "Add search engine title and description to make this collection easier to find"
                      })}
                      errors={errors}
                      slug={data.slug}
                      slugPlaceholder={data.name}
                      title={data.seoTitle}
                      titlePlaceholder={maybe(() => collection.name)}
                      onChange={change}
                    />
                  </Fragment>
                ) : (
                  ""
                )}
              </div>
              <div>
                <div>
                  <VisibilityCard
                    data={data}
                    errors={errors}
                    messages={{
                      hiddenLabel: intl.formatMessage({
                        defaultMessage: "Unpublished",
                        description: "collection label."
                      }),
                      hiddenSecondLabel: intl.formatMessage(
                        {
                          defaultMessage: "will be published on {date}",
                          description: "collection"
                        },
                        {
                          date: localizeDate(data.publicationDate, "L")
                        }
                      ),
                      visibleLabel: intl.formatMessage({
                        defaultMessage: "Published",
                        description: "collection label."
                      })
                    }}
                    onChange={change}
                  >
                    <FormSpacer />
                    <Hr />
                    <ControlledCheckbox
                      name={"isFeatured" as keyof CollectionDetailsPageFormData}
                      label={intl.formatMessage({
                        defaultMessage: "Feature on Homepage",
                        description: "switch button"
                      })}
                      checked={data.isFeatured}
                      onChange={change}
                      disabled={
                        !data.isPublished ||
                        disabled ||
                        data.isFeaturedOnSmFulfilledPage
                      }
                    />
                    {collection?.isFromShopify && (
                      <ControlledCheckbox
                        name={
                          "isFeaturedOnSmFulfilledPage" as keyof CollectionDetailsPageFormData
                        }
                        label={intl.formatMessage({
                          defaultMessage: "Feature on SM Fulfilled",
                          description: "switch button"
                        })}
                        checked={data.isFeaturedOnSmFulfilledPage}
                        onChange={change}
                        disabled={
                          !data.isPublished || disabled || data.isFeatured
                        }
                      />
                    )}
                  </VisibilityCard>
                  <CardSpacer />
                  {collection?.coverageType === CoverageTypeEnum.NATIONWIDE && (
                    <Card>
                      <CardTitle
                        title={intl.formatMessage({
                          defaultMessage: "Purchase Setup",
                          description: "Purchase Setup"
                        })}
                      />
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Checkbox
                              id="isDirectPurchaseEnabledCheckBox"
                              color="primary"
                              name="isDirectPurchaseEnabled"
                              checked={data.isDirectPurchaseEnabled}
                              onChange={e => {
                                change({
                                  target: {
                                    name: e.target.name,
                                    value: e.target.checked
                                  }
                                });
                              }}
                            />
                          }
                          label={
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6
                              }}
                            >
                              Direct Purchase
                              <Tooltip title="Product Collection with this rule will only allow ‘Buy Now’ purchases for all assigned products.">
                                <InfoOutlined fontSize="small" />
                              </Tooltip>
                            </span>
                          }
                          labelPlacement="end"
                        />
                      </CardContent>
                    </Card>
                  )}
                  <CollectionThumbnailImage
                    data={data}
                    disabled={false}
                    onChange={change}
                    errors={errors}
                    onThumbnailLinkImageUpload={onThumbnailLinkImageUpload}
                  />
                </div>
              </div>
            </Grid>
            <SaveButtonBar
              state={saveButtonBarState}
              disabled={disabled || !hasChanged}
              onCancel={onBack}
              onDelete={onCollectionRemove}
              onSave={submit}
            />
            <Dialog
              open={open}
              onClose={() => setOpen(false)}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogContent className={classes.frmTxt}>{title}</DialogContent>
              <DialogActions className={classes.frmAction}>
                <Button
                  className={classes.frmBtn}
                  id="btnNo"
                  color="primary"
                  type="button"
                  onClick={() => callback(data, false)}
                >
                  No
                </Button>
                <Button
                  className={classes.frmBtn + " active"}
                  id="btnYes"
                  color="primary"
                  type="button"
                  onClick={() => callback(data, true)}
                >
                  Yes
                </Button>
              </DialogActions>
            </Dialog>
          </Container>
        );
      }}
    </Form>
  );
};
CollectionDetailsPage.displayName = "CollectionDetailsPage";
export default CollectionDetailsPage;
